import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import env from "@beam-australia/react-env";

import { BlockUI } from "primereact/blockui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import useCRUDService from "../../../../hooks/useCRUDService";
import Layout from "../../../../layout/layout";
import useSkeleton from "../../../../hooks/useSkeleton";
import helperFormats from "../../../../utils/helperFormats";
import useApi from "../../../../hooks/useApi";

import "../../../../datatable.css";

const Credits = () => {
  const api = useApi();
  const s = useSkeleton("commons");
  const { t } = useTranslation();
  const CRUDService = useCRUDService("credits");

  const [entities, setEntities] = useState(null);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const searchInput = useRef(null);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "id",
          type: "number",
        },
        {
          name: "openedDate",
          type: "date",
        },

        {
          name: "application.amount",
          type: "number",
        },
        {
          name: "application.paymentsAmount",
          type: "number",
        },
        {
          name: "application.paymentsNumber",
          type: "number",
        },
        {
          name: "application.annualRate",
          type: "number",
        },
        {
          name: "application.annualDefaultRate",
          type: "number",
        },
        {
          name: "application.administrationFeeRate",
          type: "number",
        },
      ]);

      const response = await api.get(
        `${env("API_URL")}/website/credits/paged`,
        {
          params: {
            columns,
            page: first,
            pageSize: rows,
            sort: sortField ?? "openedDate",
            order: sortOrder >= 0 ? "ASC" : "DESC",
          },
        }
      );
      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder]);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary buttonExport"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <Layout>
        <div className="flex flex-grow-1 flex-column w-screen h-full">
          <div className="flex flex-column">
            <div className="flex justify-content-center">
              <h2 className="pageTitle">{t("website.credits.title")}</h2>
            </div>
            <div className="flex justify-content-center">
              <p>{t("website.credits.subTitle")}</p>
            </div>
          </div>
          <div className="flex justify-content-center">
            <DataTable
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              onSort={(e) => {
                setSortField(e.sortField);
                setSortOrder(e.sortOrder);
              }}
              sortField={sortField}
              sortOrder={sortOrder}
              ref={dt}
              value={entities}
              paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              columnResizeMode="fit"
              showGridlines
              dataKey="id"
              rowsPerPageOptions={[10, 25, 100, 1000]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate={`${t(
                "DataTable.currentPageReportTemplate"
              )} ${t("entities.credits.plural")}`}
              globalFilter={globalFilter}
              header={header}
              emptyMessage={t("DataTable.emptyMessage")}
              className="custom-table w-8"
            >
              <Column
                field="id"
                header={t("entities.credits.id")}
                bodyStyle={{ textAlign: "center" }}
              ></Column>
              <Column
                field="openedDate"
                header={t("entities.credits.openedDate")}
                bodyStyle={{ textAlign: "center" }}
                body={(rowData) =>
                  helperFormats.isDate(rowData.openedDate)
                    ? helperFormats.formatDate(new Date(rowData.openedDate))
                    : "Pendiente"
                }
              ></Column>
              <Column
                field="application.amount"
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats.formatDecimal(rowData.application.amount)
                }
                header={t("entities.credits.application.amount")}
              ></Column>
              <Column
                field="application.installmentsAmount"
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats.formatDecimal(
                    rowData.application.installmentsAmount
                  )
                }
                header={t("entities.credits.application.installmentsAmount")}
              ></Column>
              <Column
                field="application.installmentsNumber"
                bodyStyle={{ textAlign: "center" }}
                header={t("entities.credits.application.installmentsNumber")}
              ></Column>
              <Column
                field="application.annualRate"
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats
                    .formatDecimal(rowData.application.annualRate * 100, 0, 0)
                    .toString() + "%"
                }
                header={t("entities.credits.application.annualRate")}
              ></Column>
              <Column
                field="application.annualDefaultRate"
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats
                    .formatDecimal(
                      rowData.application.annualDefaultRate * 100,
                      0,
                      0
                    )
                    .toString() + "%"
                }
                header={t("entities.credits.application.annualDefaultRate")}
              ></Column>
              <Column
                field="application.administrationFeeRate"
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats
                    .formatDecimal(
                      rowData.application.administrationFeeRate * 100,
                      0,
                      0
                    )
                    .toString() + "%"
                }
                header={t("entities.credits.application.administrationFeeRate")}
              ></Column>
            </DataTable>
          </div>
        </div>
      </Layout>
    </React.Fragment>
  );
};

export default Credits;
